// Won guard — pick which quote the client approved before a deal can reach Won
// (Trent, Nov 2026). Accepting here runs the exact same pipeline as the quote
// screen / client portal: quote → accepted, deal → Won, install job created.
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2 } from "lucide-react";
import { fmtCurrencyExact, fmtDate } from "@/lib/constants";
import { toast } from "sonner";

export function SelectAcceptedQuoteDialog({ guard, onClose, onAccepted }) {
  const [picked, setPicked] = useState(null);
  useEffect(() => { if (guard) setPicked(guard.quotes?.length === 1 ? guard.quotes[0].id : null); }, [guard]);
  const accept = useMutation({
    mutationFn: async (qid) => (await api.patch(`/quotes/${qid}`, { status: "accepted" })).data,
    onSuccess: async (quote) => {
      let jobId = quote.accepted_job_id || null;
      if (!jobId) {
        try {
          const { data: jobs } = await api.get("/jobs");
          jobId = jobs.find((j) => j.quote_id === quote.id)?.id || null;
        } catch { /* toast without job link */ }
      }
      onAccepted(jobId, quote);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not accept the quote"),
  });
  const quotes = guard?.quotes || [];
  return (
    <Dialog open={!!guard} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm sm:max-w-xl" data-testid="won-guard-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Which quote did the client approve?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">
          A deal can't be marked <b className="text-slate-700">Won</b> without an accepted quote. Pick the quote the
          client approved — it'll be marked <b className="text-slate-700">Accepted</b>, the deal moves to{" "}
          <b className="text-slate-700">Won</b> and the install job is created, exactly like an acceptance from the
          quote screen or client portal.
        </p>
        {quotes.length === 0 ? (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5" data-testid="won-guard-no-quotes">
            This deal has <b>no sent quotes</b> — create and send a quote first. The deal stays in its current stage
            until a quote is accepted.
          </div>
        ) : (
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            {quotes.map((q) => {
              const sel = picked === q.id;
              return (
                <button key={q.id} type="button" onClick={() => setPicked(q.id)}
                  className={`w-full flex items-center gap-3 text-left border rounded-sm px-3.5 py-3 transition-colors ${sel ? "border-cyan-500 bg-cyan-50/60 ring-1 ring-cyan-500" : "border-slate-200 hover:border-cyan-300"}`}
                  data-testid={`won-guard-quote-${q.id}`}>
                  <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${sel ? "border-cyan-600" : "border-slate-300"}`}>
                    {sel && <span className="w-2 h-2 rounded-full bg-cyan-600" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-cyan-700">{q.quote_number}</span>
                      <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-800 uppercase">{q.status}</Badge>
                    </span>
                    <span className="block text-[11px] text-slate-500 mt-0.5 truncate">
                      Rev {q.revision || 1}{q.sent_at ? ` · sent ${fmtDate(q.sent_at)}` : ""}{q.fence_type ? ` · ${q.fence_type}` : ""}
                    </span>
                  </span>
                  {q.sale_price != null && <span className="font-mono text-sm font-bold text-slate-900 shrink-0">{fmtCurrencyExact(q.sale_price)}</span>}
                </button>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="won-guard-cancel-btn">
            Cancel — keep current stage
          </Button>
          {quotes.length > 0 && (
            <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!picked || accept.isPending}
              onClick={() => accept.mutate(picked)} data-testid="won-guard-accept-btn">
              {accept.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Accept quote &amp; mark Won
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
